import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["grid", "row", "rowTemplate"];
  static values = {
    questionPartId: String,
    size: Number,
    alternativeDefaultText: String
  };

  connect() {
    this._updateGridClass(this.sizeValue);
    this._updateRowSpans(this.sizeValue);
  }

  changeSize(event) {
    const newSize = parseInt(event.currentTarget.dataset.value, 10);
    if (isNaN(newSize) || newSize < 1 || newSize > 11) return;

    const visibleRows = this.rowTargets.filter(r => !r.classList.contains('hidden'));
    const currentSize = visibleRows.length;
    const delta = newSize - currentSize;

    if (delta > 0) {
      this._grow(currentSize, delta);
    } else if (delta < 0) {
      this._shrink(Math.abs(delta));
    }

    this.sizeValue = newSize;
    this._updateGridClass(newSize);
    this._updateRowSpans(newSize);
  }

  _grow(currentSize, delta) {
    const hiddenRows = this.rowTargets.filter(r => r.classList.contains('hidden'));

    for (let i = 0; i < delta; i++) {
      const hiddenRow = hiddenRows[i];
      if (hiddenRow) {
        this._showRow(hiddenRow, currentSize + i);
      } else {
        this._cloneRow(currentSize + i);
      }
    }
  }

  _shrink(count) {
    const visibleRows = this.rowTargets.filter(r => !r.classList.contains('hidden'));

    for (let i = 0; i < count; i++) {
      const row = visibleRows[visibleRows.length - 1 - i];
      const altId = row.dataset.alternativeId;

      if (altId) {
        const destroyField = row.querySelector('[data-destroy-field]');
        if (destroyField) {
          destroyField.value = '1';
        }

        row.querySelectorAll('input[type="text"]').forEach(input => {
          input.disabled = true;
        });

        row.classList.add('hidden');
      } else {
        row.remove();
      }
    }
  }

  _showRow(row, newIndex) {
    const destroyField = row.querySelector('[data-destroy-field]');
    if (destroyField) {
      destroyField.value = 'false';
    }

    row.querySelectorAll('input[type="text"]').forEach(input => {
      input.disabled = false;
    });

    row.classList.remove('hidden');

    const positionField = row.querySelector('input[name*="[position]"]');
    if (positionField) positionField.value = newIndex;
  }

  _cloneRow(index) {
    const template = this.rowTemplateTarget;
    const clone = template.content.cloneNode(true).firstElementChild;
    const uniqueKey = Date.now() + index;

    clone.querySelectorAll('input').forEach(input => {
      input.name = input.name.replace('NEW_IDX', uniqueKey);
    });

    const positionField = clone.querySelector('input[name*="[position]"]');
    if (positionField) {
      positionField.value = index;
    }

    const textField = clone.querySelector('input[name*="[text]"]');

    if (textField) {
      textField.value = this.alternativeDefaultTextValue.replace('__INDEX__', index + 1);
    }

    this.gridTarget.appendChild(clone);
  }

  _updateGridClass(n) {
    this.gridTarget.classList.remove('grid-cols-2', 'grid-cols-6');
    this.gridTarget.classList.add(n === 4 ? 'grid-cols-2' : 'grid-cols-6');
  }

  _updateRowSpans(n) {
    // Query the DOM directly so newly appended clones are included regardless of
    // when Stimulus's MutationObserver updates rowTargets.
    const visibleRows = Array.from(
      this.gridTarget.querySelectorAll(
        '[data-questionnaires--scale-options-target="row"]:not(.hidden)'
      )
    );

    const defaultSpan = n === 4 ? 'col-span-1' : 'col-span-2';

    visibleRows.forEach(row => {
      row.classList.remove('col-span-1', 'col-span-2', 'col-span-3', 'col-span-6');
      row.classList.add(defaultSpan);
    });

    if (n === 4) return; // 2-col grid, each item takes 1 column

    const remainder = n % 3;
    if (remainder === 0) return; // perfect fit, no partial row

    const lastItems = visibleRows.slice(-remainder);

    if (remainder === 1) {
      // Single item in last row => full width (N=7, N=10)
      lastItems[0].classList.remove('col-span-2');
      lastItems[0].classList.add('col-span-6');
    } else {
      // Two items in last row => each half width (N=5, N=8)
      lastItems.forEach(row => {
        row.classList.remove('col-span-2');
        row.classList.add('col-span-3');
      });
    }
  }
}
